/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import React, { useContext, useMemo, useRef } from 'react';
// temporary disabling this until I can come up with a better api
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { ThemeContext } from '@cvent/carina-theme-provider';
import { jsx } from '@emotion/core';
import { getStyle } from './spinnerGetStyle.js';
import { BreakpointsContext } from '@cvent/carina-layout';

const LoadingSpinnerApi = {
  /** the size of the loading spinner. Note, medium(m) and large(l) are not the only options for the spinner, if you want something that a lot smaller, you can use the icon version, in one of quite a few color options. The medium size is 80px and the large size is 180px */
  size: PropTypes.oneOf(['m', 'l']),
  /** the id. if you don't assign one, then a random id will be generated  */
  id: PropTypes.string,
  /** the test id, for testing */
  testID: PropTypes.string,
  /** the text that appears next to the loading spinner. */
  text: PropTypes.string,
  /** if the text should appear on the side of the spinner instead of on the bottom. Note that this is a space aware feature, so if you have text that would overflow on the right, it will default to the bottom, and if that text on the bottom would overflow, it wraps lines. It will never truncate text, as per design, so make sure to follow the guideline and not pass in a whole bunch of text. */
  textInline: PropTypes.bool
};
export const LoadingSpinner = ({
  size,
  testID: testIdProp,
  text,
  textInline,
  /* eslint-disable react/prop-types */
  // standard props
  id,
  /* enable-disable react/prop-types */
  ...propDrop
}) => {
  let keyCount = 1;
  function getKey() {
    if (keyCount < Number.MAX_SAFE_INTEGER - 1) {
      return keyCount++;
    }
    return (keyCount = 1);
  }

  const strokeId = useRef(id ? `${id}-gradient` : `${getKey()}-gradient`);
  const testID = testIdProp || id;

  const {
    outerContainerStyle,
    circleStyle,
    tailChaserStyle,
    textStyle,
    gradientColor1,
    gradientColor2,
    gradientColor3
  } = useStyle({ textInline, size });
  return (
    <div
      {...propDrop}
      id={id}
      {...injectTestId(testID)}
      role="progressbar"
      css={outerContainerStyle}
    >
      <svg xmlns="http://www.w3.org/2000/svg" css={circleStyle}>
        <defs>
          <linearGradient
            id={strokeId.current}
            x1="0%"
            y1="100%"
            x2="0%"
            y2="0%"
          >
            <stop offset="0%" stopColor={gradientColor1} />
            <stop offset="50%" stopColor={gradientColor2} />
            <stop offset="100%" stopColor={gradientColor3} />
          </linearGradient>
        </defs>
        <circle cx="50%" cy="50%" r={'50%'} />
        <circle
          stroke={`url(#${strokeId.current})`}
          cx="50%"
          cy="50%"
          r={'50%'}
          css={tailChaserStyle}
        />
      </svg>
      {text && size === 'm' && <h4 css={textStyle}>{text}</h4>}
    </div>
  );
};

LoadingSpinner.propTypes = LoadingSpinnerApi;

function useStyle({ size, textInline }) {
  const theme = useContext(ThemeContext);
  const { prefersReducedMotion } = useContext(BreakpointsContext);
  const {
    outerContainer,
    circle,
    tailChaser,
    text,
    gradientColor1,
    gradientColor2,
    gradientColor3,
    rail
  } = useMemo(() => getStyle(theme), [theme]);
  return {
    outerContainerStyle: {
      ...outerContainer.base,
      ...(size === 'l' ? outerContainer.big : {}),
      ...(textInline ? outerContainer.inline : {})
    },
    circleStyle: {
      ...circle.base,
      ...(size === 'l' ? circle.big : {}),
      ...(prefersReducedMotion ? circle.slow : {})
    },
    tailChaserStyle: {
      ...tailChaser.base,
      ...(size === 'l' ? tailChaser.big : {})
    },
    textStyle: {
      ...text.base,
      ...(textInline ? text.inline : {})
    },
    gradientColor1,
    gradientColor2,
    gradientColor3,
    rail
  };
}
